import React from 'react';

export interface TickerProps {
  firstText: string;
  textColor: string;
  repetitionFactor: number;
  children?: React.ReactNode;
  backgroundColor: string;
  heightBanner?: '1x' | '1.25x' | '1.5x' | '1.75x' | '2x';
}

const parseTextWithLinks = (text?: string, itemIndex = 0): React.ReactNode => {
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const matches = Array.from(text?.matchAll(linkRegex) ?? []);

  if (matches.length === 0) {
    return text ?? '';
  }

  const markdownMatches = matches.reduce<React.ReactNode[]>((parts, match, index) => {
    const [fullMatch, linkText, url] = match;
    const matchStart = match.index!;
    const matchEnd = matchStart + fullMatch.length;

    const prevEnd = index === 0 ? 0 : matches[index - 1].index! + matches[index - 1][0].length;
    if (matchStart > prevEnd) {
      parts.push(text?.slice(prevEnd, matchStart) ?? '');
    }

    parts.push(
      <a
        key={matchStart}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline hover:no-underline"
        data-testid={`ticker-cta-${itemIndex}-${index}`}
      >
        {linkText}
      </a>,
    );

    if (index === matches.length - 1 && matchEnd < (text?.length ?? 0)) {
      parts.push(text?.slice(matchEnd) ?? '');
    }

    return parts;
  }, []);

  return markdownMatches;
};

const Ticker: React.FC<TickerProps> = ({
  firstText,
  textColor,
  repetitionFactor,
  backgroundColor,
  heightBanner = '1x',
}) => {
  const texts = Array(repetitionFactor).fill(firstText);

  const heightBannerClass = {
    '1x': 'h-48',
    '1.25x': 'h-64',
    '1.5x': 'h-72',
    '1.75x': 'h-88',
    '2x': 'h-96',
  };

  return (
    <div className="tcontainer w-full overflow-hidden" data-testid="ticker">
      <div
        className={`ticker-wrap ${heightBannerClass[heightBanner]} w-full ${
          backgroundColor ? '' : 'bg-ticker-default'
        } flex items-center`}
        style={{ backgroundColor: backgroundColor }}
      >
        <div className="ticker-move animate-ticker inline-block whitespace-nowrap">
          <div className="flex justify-between">
            {!!repetitionFactor &&
              texts.map((text, index) => (
                <div key={index} className="ticker-item" data-testid={`ticker-item-${index}`}>
                  <span className="mr-5 text-black" style={{ color: textColor }}>
                    {parseTextWithLinks(text, index)}
                  </span>
                </div>
              ))}
          </div>
        </div>
        {!repetitionFactor && (
          <div className="ticker-item px-16 md:px-24 lg:px-48" data-testid="ticker-item-0">
            <span className="mr-5 text-black" style={{ color: textColor }}>
              {parseTextWithLinks(firstText)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Ticker;
